import React, { useEffect, useState } from "react";
import { usePageMeta } from "@/lib/usePageMeta";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useI18n();
  usePageMeta({ title: t("reset_title"), noindex: true });
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Supabase reads the recovery token out of the URL fragment client-side
    // and fires PASSWORD_RECOVERY once the recovery session is established.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setReady(true);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    return () => subscription?.unsubscribe();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6 || password !== confirm) {
      toast.error(t("password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(error.message || t("auth_error"));
        return;
      }
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2000);
    } catch {
      toast.error(t("auth_error"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-sm mx-auto py-24 text-center animate-in fade-in duration-500">
        <h1 className="text-2xl font-serif-en mb-3">{t("reset_title")}</h1>
        <p className="text-sm text-muted-foreground" data-testid="reset-success-msg">{t("reset_success")}</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto py-16 px-4 sm:px-0 animate-in fade-in duration-500">
      <h1 className="text-2xl font-serif-en mb-6 text-center">{t("reset_title")}</h1>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="reset-password" className="text-sm text-muted-foreground">{t("reset_new_password")}</label>
          <Input id="reset-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
            autoComplete="new-password" data-testid="reset-password-input" />
        </div>
        <div>
          <label htmlFor="reset-confirm" className="text-sm text-muted-foreground">{t("reset_confirm_password")}</label>
          <Input id="reset-confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={6}
            autoComplete="new-password" data-testid="reset-confirm-input" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit-btn">
          {loading ? "…" : t("reset_submit")}
        </Button>
      </form>
    </div>
  );
}
